import { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  InputAdornment,
} from "@mui/material";
import {
  useGetHobbiesQuery,
  useCreateHobbyMutation,
  useUpdateHobbyMutation,
  useDeleteHobbyMutation,
  Hobby,
} from "../../../entities/admin-hobbies";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

export const HobbiesManagement = () => {
  const [selectedHobby, setSelectedHobby] = useState<Hobby | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Form states
  const [hobbyName, setHobbyName] = useState("");

  // Queries and mutations
  const { data: hobbiesData, isLoading: isLoadingHobbies } =
    useGetHobbiesQuery();

  const [createHobby, { isLoading: isCreating }] = useCreateHobbyMutation();
  const [updateHobby, { isLoading: isUpdating }] = useUpdateHobbyMutation();
  const [deleteHobby, { isLoading: isDeleting }] = useDeleteHobbyMutation();

  // Filtered hobbies based on search query
  const filteredHobbies = useMemo(() => {
    if (!hobbiesData?.hobbies) return [];

    return hobbiesData.hobbies.filter((hobby) =>
      hobby.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [hobbiesData?.hobbies, searchQuery]);

  // Handlers
  const handleSelectHobby = (hobby: Hobby) => {
    setSelectedHobby(hobby);
  };

  const handleOpenCreateDialog = () => {
    setHobbyName("");
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (hobby: Hobby) => {
    setSelectedHobby(hobby);
    setHobbyName(hobby.name);
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (hobby: Hobby) => {
    setSelectedHobby(hobby);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const isFormValid = () => {
    return hobbyName.trim() !== "";
  };

  const handleCreateHobby = async () => {
    if (!isFormValid()) return;

    try {
      await createHobby({
        name: hobbyName.trim(),
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating hobby:", error);
    }
  };

  const handleUpdateHobby = async () => {
    if (!selectedHobby || !isFormValid()) return;

    try {
      await updateHobby({
        id: selectedHobby.id,
        body: {
          name: hobbyName.trim(),
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating hobby:", error);
    }
  };

  const handleDeleteHobby = async () => {
    if (!selectedHobby) return;

    try {
      await deleteHobby(selectedHobby.id).unwrap();
      handleCloseDialogs();
      setSelectedHobby(null);
    } catch (error) {
      console.error("Error deleting hobby:", error);
    }
  };

  // Render hobby form
  const renderHobbyForm = () => (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Название"
          fullWidth
          value={hobbyName}
          onChange={(e) => setHobbyName(e.target.value)}
          margin="normal"
        />
      </Grid>
    </Grid>
  );

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog
      open={openCreateDialog}
      onClose={handleCloseDialogs}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Добавить хобби</DialogTitle>
      <DialogContent>{renderHobbyForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleCreateHobby}
          disabled={!isFormValid() || isCreating}
        >
          {isCreating ? <CircularProgress size={24} /> : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog
      open={openEditDialog}
      onClose={handleCloseDialogs}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Редактировать хобби</DialogTitle>
      <DialogContent>{renderHobbyForm()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleUpdateHobby}
          disabled={!isFormValid() || isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box
      sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление хобби
      </Typography>

      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <Paper
          sx={{ width: "100%", p: 2, display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography fontWeight={600} fontSize={18}>
              Список хобби
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Добавить
            </Button>
          </Box>

          <TextField
            placeholder="Поиск хобби"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {isLoadingHobbies ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredHobbies.length ? (
            <Box
              sx={{
                overflow: "auto",
                flexGrow: 1,
                height: 0, // This forces the box to take available space and scroll
              }}
            >
              <List sx={{ padding: 0 }}>
                {filteredHobbies.map((hobby) => (
                  <ListItem
                    key={hobby.id}
                    sx={{
                      borderRadius: 1,
                      backgroundColor:
                        selectedHobby?.id === hobby.id ? "#515151" : "inherit",
                      "&:hover": {
                        cursor: "pointer",
                        backgroundColor: "#414141",
                      },
                      mb: 1,
                    }}
                    onClick={() => handleSelectHobby(hobby)}
                    secondaryAction={
                      <Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditDialog(hobby);
                          }}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteDialog(hobby);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={hobby.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Typography color="text.secondary" align="center" py={3}>
              {searchQuery ? "Хобби не найдены" : "Хобби не найдены"}
            </Typography>
          )}
        </Paper>
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
      {openDeleteDialog && selectedHobby && (
        <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
          <DialogTitle>Удалить хобби</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить хобби "{selectedHobby.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Отмена</Button>
            <Button
              color="error"
              onClick={handleDeleteHobby}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={24} /> : "Удалить"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
